// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { Benefit, ErrorCause, Feedback, Transaction, TransactionPayload } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { AutocompleteTags } from '../../fields/AutocompleteTags/AutocompleteTags';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// utils
import { postOrGetTags } from '../../../utils/tags';

// props
export interface FormEditNonCashBenefitValues {
	benefit: string;
	quantity: number;
	tags: string[];
}

export interface FormEditNonCashBenefitProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	transaction: Transaction;
}

export const FormEditNonCashBenefit = ({
	onClose,
	onSuccess,
	setIsLoading,
	transaction,
}: FormEditNonCashBenefitProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [benefits, setBenefits] = useState<Benefit[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		benefit: '',
		quantity: transaction.quantity || 0,
		tags: transaction.tags ? transaction.tags.map((tag) => tag.name) : [],
	});

	// vars
	const idForm = 'form-edit-non-cash-benefit';

	// fetch benefits
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/benefits?project=${project?.id}&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBenefits(res.data);
			}
		},
	});

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const optionsBenefits = benefits.map((benefit) => {
		return {
			label: benefit.name,
			value: String(benefit.id),
		};
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setFeedback(null);

			// set loading
			setIsLoading(true);

			let tags: number[] = [];

			if (entries.tags && project) {
				// post or get tags
				const resTags = await postOrGetTags({
					names: (entries.tags as string).split(','),
					project: project.id,
				});

				if (resTags.length) {
					// define tags ids
					tags = resTags.map((tag) => tag.id);
				}
			}

			const body: TransactionPayload = {
				benefit: Number(entries.benefit),
				quantity: Number(entries.quantity),
				tags,
			};

			const fetchEditTransaction = await fetch(
				`${process.env.REACT_APP_API_URL}/transactions/${transaction.id}`,
				{
					method: 'PATCH',
					body: JSON.stringify(body),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			const resEditTransaction = await fetchEditTransaction.json();

			if (resEditTransaction.error) {
				throw new Error(resEditTransaction.error.message, {
					cause: {
						id: resEditTransaction.error.id,
						details: resEditTransaction.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.nonCashBenefitUpdated'),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause.id === 'TRANSACTION_ID_NON_EXISTENT') {
				feedbackNew.alert.children = t('error.transactionIdNonExistent');
			}

			if (cause.id === 'TRANSACTION_UNAUTHORIZED_PATCH') {
				feedbackNew.alert.children = t('error.unauthorized');
			}

			// set error
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	// set values when options exist
	useEffect(() => {
		const valuesNew: Partial<FormEditNonCashBenefitValues> = {};

		if (benefits.length && transaction.benefit) {
			valuesNew.benefit = String((transaction.benefit as Benefit)?.id);
		}

		setValues((valuesPrev) => {
			return {
				...valuesPrev,
				...valuesNew,
			};
		});
	}, [benefits, transaction]);

	// Update tags when transaction changes
	useEffect(() => {
		if (transaction) {
			setValues((prevValues) => ({
				...prevValues,
				tags: transaction.tags ? transaction.tags.map((tag) => tag.name) : [],
			}));
		}
	}, [transaction]);

	return (
		<form id={idForm} name="formEditNonCashBenefit" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-quantity`}
						inputProps={{
							min: 1,
						}}
						label={t('quantity')}
						name="quantity"
						onChange={handleChangeValue}
						required={true}
						type="number"
						value={values.quantity}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-benefit`}
						label={t('benefit')}
						name="benefit"
						onChange={handleChangeValue}
						options={optionsBenefits}
						required={true}
						value={values.benefit}
					/>
				</Grid>
				{userProjectPermissions?.tag?.create && (
					<Grid item xs={12}>
						<AutocompleteTags
							id={`${idForm}-tags`}
							onChange={(newTags) => {
								setValues((prevState) => ({
									...prevState,
									tags: newTags,
								}));
							}}
							value={values.tags}
						/>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
